"use client";

import { useRef, useState } from "react";
import { useZipGeocoding } from "@/hooks/use-zip-geocoding";
import type { Dock } from "@/lib/listings/builder-state";
import { cn } from "@/lib/utils";

interface DockFieldProps {
  dock: Dock | null;
  onDock: (dock: Dock | null) => void;
  onPending: (pending: boolean) => void;
}

function useDockLookup(
  dock: Dock | null,
  onDock: (dock: Dock | null) => void,
  onPending: (pending: boolean) => void,
) {
  const { geocodeZip, isLoading } = useZipGeocoding();
  const [value, setValue] = useState(dock?.postalCode ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const lookup = useRef(0);

  const edit = (next: string) => {
    lookup.current += 1;
    onPending(false);
    setProblem(null);
    setValue(next);
    if (dock && next.trim() !== dock.postalCode) onDock(null);
  };

  async function resolve() {
    if (!value.trim()) return onDock(null);
    if (dock && dock.postalCode === value.trim()) return;
    const ticket = ++lookup.current;
    onPending(true);
    const postalCode = value.trim();
    const location = await geocodeZip(postalCode);
    if (ticket === lookup.current) onPending(false);
    if (ticket !== lookup.current) return;
    if (location) {
      setProblem(null);
      return onDock({
        postalCode: location.postalCode,
        lat: location.lat,
        lng: location.lng,
        state: location.state,
        label: `${location.city}, ${location.state}`,
      });
    }
    if (!/^\d{5}$/.test(postalCode)) {
      onDock(null);
      return setProblem("Enter a 5-digit US ZIP");
    }
    setProblem("Couldn't locate that ZIP right now; your quote will carry it as entered.");
    onDock({ postalCode, label: `ZIP ${postalCode}` });
  }

  return { value, edit, resolve, problem, isLoading };
}

export function DockField({ dock, onDock, onPending }: DockFieldProps) {
  const { value, edit, resolve, problem, isLoading } = useDockLookup(dock, onDock, onPending);
  return (
    <div>
      <label htmlFor="dock-zip" className="text-overline mb-1 block uppercase text-chrome-muted">
        Deliver to ZIP
      </label>
      <input
        id="dock-zip"
        inputMode="numeric"
        value={value}
        onChange={(event) => edit(event.target.value)}
        onBlur={resolve}
        onKeyDown={(event) => event.key === "Enter" && resolve()}
        placeholder="43215"
        className="h-9 w-full rounded-md border border-chrome-border bg-chrome-2 px-2.5 text-sm text-chrome-foreground placeholder:text-chrome-muted/60"
        aria-describedby="dock-zip-status"
      />
      <p
        id="dock-zip-status"
        role="status"
        aria-live="polite"
        className={cn("mt-1 min-h-4 text-xs", problem ? "text-chrome-foreground" : "text-chrome-muted")}
      >
        {problem ?? (isLoading ? "Locating…" : dock?.label ?? "")}
      </p>
    </div>
  );
}
